import createDOMPurify from 'dompurify';

// Create a single shared DOMPurify instance with SSR-safe window fallback
const win = typeof window !== 'undefined' ? window : {};
const purifier = createDOMPurify(win);

// Configure DOMPurify with our security settings
const CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 'code', 'pre', 'blockquote',
    'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'img'
  ],
  ALLOWED_ATTR: ['href', 'title', 'src', 'alt', 'target', 'rel'],
  ALLOWED_URI_REGEXP: /^(https?|mailto|tel):/i,
  // Use DOMPurify's built-in XSS protection instead of FORBID_ATTR
  // which was causing conflicts with allowed attributes
};

// Add hook to enforce noopener noreferrer on links with target="_blank"
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Sanitizes plain text by removing all HTML tags and dangerous content
 * Use this for text that should not contain any HTML
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text with no HTML tags
 */
export const sanitizeText = (text = '') => {
  return purifier.sanitize(text, { ALLOWED_TAGS: [] });
};

/**
 * Sanitizes HTML content while allowing safe formatting tags
 * Use this for rich text content that may contain formatting
 * @param {string} html - The HTML to sanitize
 * @returns {string} - Sanitized HTML with only safe tags
 */
export const sanitizeHTML = (html = '') => {
  return purifier.sanitize(html, CONFIG);
};

/**
 * Sanitizes attributes for use in HTML attributes (alt, title, aria-label, etc.)
 * This is extra strict to prevent attribute-based XSS
 * @param {string} text - The text to sanitize for attributes
 * @returns {string} - Sanitized text safe for HTML attributes
 */
export const sanitizeAttribute = (text = '') => {
  // Remove all HTML tags and encode special characters
  const cleaned = purifier.sanitize(text, { ALLOWED_TAGS: [] });
  // Additional encoding for attribute context
  return cleaned
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
};

/**
 * Sanitizes user input for display while preserving line breaks
 * Use this for user-generated content like posts, comments, etc.
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text with line breaks preserved as <br>
 */
export const sanitizeUserContent = (text = '') => {
  // First replace newlines with <br>, then sanitize the entire content
  return purifier.sanitize(text.replace(/\n/g, '<br>'), CONFIG);
};

/**
 * Validates and sanitizes a URL
 * @param {string} url - The URL to validate and sanitize
 * @returns {string|null} - Sanitized URL or null if invalid
 */
export const sanitizeURL = (url) => {
  if (!url) return null;
  
  try {
    const parsed = new URL(url);
    // Only allow http(s), mailto, and tel protocols
    if (!['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol)) {
      return null;
    }
    return parsed.toString();
  } catch {
    // If URL parsing fails, it's not a valid URL
    return null;
  }
};